import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, UsersRound, Shield, ShieldCheck, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const roles = ["Owner", "Partner", "Employee"];
const roleIcons = { Owner: ShieldCheck, Partner: Shield, Employee: UserIcon };
const roleColors = {
  Owner: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  Partner: "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  Employee: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
};

export default function Team() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "Employee", password: "" });

  async function load() { const { data } = await api.get("/users"); setUsers(data); }
  useEffect(() => { load(); }, []);

  const canManage = me?.role === "Owner" || me?.role === "Partner";

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Team member added");
      setOpen(false);
      setForm({ name: "", email: "", role: "Employee", password: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  async function del(id) {
    if (!confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  return (
    <div className="space-y-5" data-testid="team-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <UsersRound className="w-3 h-3" /> Firm members
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Team</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage practice users, roles and permissions.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn" className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1.5" />Add User</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Invite team member</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div><Label>Full Name *</Label><Input data-testid="user-name-input" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Email *</Label><Input data-testid="user-email-input" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Role</Label>
                  <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{roles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Temporary Password *</Label>
                  <Input data-testid="user-password-input" type="text" required minLength={6} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="min 6 chars" />
                  <div className="text-[10px] text-muted-foreground mt-1">User can change this after first login.</div>
                </div>
                <DialogFooter><Button data-testid="save-user-btn" type="submit" className="bg-blue-600 hover:bg-blue-700">Create user</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map(u => {
          const RoleIcon = roleIcons[u.role] || UserIcon;
          return (
            <Card key={u.id} className="p-5 border-l-[3px] border-blue-600" data-testid={`user-card-${u.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-blue-600 text-white grid place-items-center font-display font-bold text-lg">
                  {u.name?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold truncate">{u.name}</div>
                  <div className="text-xs text-muted-foreground truncate font-mono">{u.email}</div>
                </div>
                <Badge className={`${roleColors[u.role]} border-0 text-[10px]`}>
                  <RoleIcon className="w-3 h-3 mr-1 inline" />{u.role}
                </Badge>
              </div>
              {me?.role === "Owner" && u.id !== me.id && (
                <div className="mt-3 pt-3 border-t border-border flex justify-end">
                  <Button size="sm" variant="ghost" onClick={() => del(u.id)} className="h-7 text-[11px] text-red-600">
                    <Trash2 className="w-3 h-3 mr-1" /> Remove
                  </Button>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
